/**
 * API access.
 *
 * Every call is a same-origin relative path. The dev server proxies them and the
 * production container serves them directly, so no code branches on environment.
 */

import type {
  BenchmarksResponse,
  EscalationDraft,
  NetworkConsole,
  ContractProposal,
  ExtractionResult,
  Portfolio,
  Session,
  SourcesResponse,
  Vendor,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The server's own words for a failure.
 *
 * FastAPI wraps them in a `detail`, and a refusal carries an object rather than a
 * string. Unwrapping here is what keeps a raw JSON blob off the screen when the
 * platform declines to do something.
 */
export async function errorMessage(response: Response): Promise<string> {
  const body = await response.text().catch(() => "");
  try {
    const detail = (JSON.parse(body) as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === "string") return message;
    }
  } catch {
    // Not JSON. The raw body is the best available description.
  }
  return body || response.statusText;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    throw new ApiError(await errorMessage(response), response.status);
  }
  return (await response.json()) as T;
}

export function fetchPortfolio(tenant?: string | null): Promise<Portfolio> {
  const query = tenant ? `?tenant=${encodeURIComponent(tenant)}` : "";
  return request<Portfolio>(`/api/portfolio${query}`);
}

export function fetchVendor(vendorId: string): Promise<Vendor> {
  return request<Vendor>(`/api/vendors/${encodeURIComponent(vendorId)}`);
}

export function fetchSources(): Promise<SourcesResponse> {
  return request<SourcesResponse>("/api/sources");
}

/**
 * Compose a draft. The server decides what may go in it, not this call: passing a
 * vendor-attested service level here gets it refused, not drafted.
 */
export function draftEscalation(
  vendorId: string,
  serviceLevelIds: string[],
): Promise<EscalationDraft> {
  return request<EscalationDraft>(`/api/vendors/${encodeURIComponent(vendorId)}/escalation`, {
    method: "POST",
    body: JSON.stringify({ service_level_ids: serviceLevelIds }),
  });
}

export function fetchNetworkConsole(): Promise<NetworkConsole> {
  return request<NetworkConsole>("/api/network/console");
}

export function fetchBenchmarks(tenant: string): Promise<BenchmarksResponse> {
  return request<BenchmarksResponse>(
    `/api/network/benchmarks?tenant=${encodeURIComponent(tenant)}`,
  );
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export function askAnalyst(
  messages: ChatMessage[],
  tenant: string | null,
): Promise<{ content: string }> {
  return request<{ content: string }>("/api/chat", {
    method: "POST",
    body: JSON.stringify({ messages, tenant }),
  });
}

/** The current session, or null when nobody is signed in. */
export async function fetchSession(): Promise<Session | null> {
  try {
    return await request<Session>("/api/auth/me");
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

/** Where to send the browser to sign in. Keycloak owns the form. */
export function loginUrl(next: string = "/"): string {
  return `/api/auth/login?next=${encodeURIComponent(next)}`;
}

/**
 * End the session here, then at Keycloak.
 *
 * Clearing only the local cookie would leave the identity provider's session intact, so
 * the next sign-in would skip the password prompt and look as though logout had failed.
 */
export async function logout(): Promise<string> {
  const response = await request<{ logout_url: string }>("/api/auth/logout", {
    method: "POST",
  });
  return response.logout_url;
}

/** Read an exhibit. Proposes only; nothing is stored until it is confirmed. */
export function extractContract(document: string): Promise<ExtractionResult> {
  return request<ExtractionResult>("/api/onboarding/extract", {
    method: "POST",
    body: JSON.stringify({ document }),
  });
}

/** Persist a proposal a person has reviewed. */
export function createVendor(
  proposal: ContractProposal,
  tenantId: string | null,
  sourceDocument: string,
): Promise<{ vendor_id: string; tenant_id: string }> {
  return request<{ vendor_id: string; tenant_id: string }>("/api/onboarding/vendors", {
    method: "POST",
    body: JSON.stringify({
      tenant_id: tenantId,
      proposal,
      source_document: sourceDocument.slice(0, 200_000),
    }),
  });
}
